import { type FormProps, Modal } from 'antd'
import React, { useEffect, useState } from 'react'

import { type BundleUpdateParams, BundleService } from '@services/BundleService'
import { useNotifications } from '@store/notifications'

import type { Curriculum } from '../../../../types'
import { type BundleFieldType, BundleForm } from '../components/BundleForm'

type ModalProps = {
    selectedId: string | null
    onClose: () => void
    onSuccess: () => void
    curriculums?: Curriculum[]
}

export const EditBundleModal: React.FC<ModalProps> = ({ selectedId, onClose, onSuccess, curriculums }) => {
    const [data, setData] = useState<BundleFieldType>()
    const { showNotification } = useNotifications()

    useEffect(() => {
        const fetch = async () => {
            try {
                const results = await BundleService.get(selectedId!)

                const { bundleCurriculums, ...rest } = results
                setData({ ...rest, curriculumIds: bundleCurriculums.map(bc => bc.curriculumId) })
            } catch (err: any) {
                showNotification({ title: 'Could not retrieve bundle', type: 'error', description: err.toString() })
            }
        }

        if (selectedId) {
            fetch()
        }
    }, [selectedId])

    const onFinish: FormProps<BundleFieldType>['onFinish'] = async values => {
        const body: BundleUpdateParams = {
            name: values.name,
            description: values.description,
            order: values.order,
            creatorId: values.creatorId,
            bundleCurriculums: values.curriculumIds.map(curriculumId => ({ curriculumId }))
        }

        if (values.meta?.links) {
            body.meta = { links: values.meta.links }
        }

        try {
            await BundleService.update(selectedId!, body)
            showNotification({ title: 'Bundle updated successfully' })
            onSuccess()
            onClose()
        } catch (err: any) {
            showNotification({ title: 'Bundle update failed', type: 'error', description: err.toString() })
        }
    }

    const isOpen = !!selectedId

    if (!data) {
        return null
    }

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose} width="700px">
            <BundleForm
                title="Edit Bundle"
                initialValues={data}
                onFinish={onFinish}
                onCancel={onClose}
                curriculums={curriculums}
            />
        </Modal>
    )
}
